import {
  PASSPORT_CONFIG,
  type PassportContext,
  type PassportData,
  type PassportExtraction,
} from "@asap/shared/document/document-types/passport";
import { PassportSchemas } from "@asap/shared/schemas/document/document-types/passport";
import { countryOptions, nationalityOptions } from "@asap/shared/utils/countries";
import { formatFirstName, formatLastName } from "@asap/shared/utils/string";
import { DocumentTypeHandler } from "../handler";

/**
 * Passport Handler
 *
 * Similar to National ID Card but:
 * - Only 1 file (no recto/verso)
 * - Simpler transformExtraction (no hasRecto/hasVerso validation)
 */
export const PassportHandler = new DocumentTypeHandler<PassportExtraction, PassportData, PassportContext>({
  // Identity
  documentType: PASSPORT_CONFIG.documentType,
  documentName: PASSPORT_CONFIG.documentName,

  // Constraints
  maxFiles: PASSPORT_CONFIG.maxFiles,
  acceptedMimeTypes: PASSPORT_CONFIG.acceptedMimeTypes,
  requiredFields: PASSPORT_CONFIG.requiredFields,

  // API endpoints
  endpoints: {
    extract: "documents/passport/extract",
    warn: "documents/passport/warn",
    verify: "documents/passport/verify",
    store: "documents/passport/store",
  },

  // Response schemas for runtime validation
  schemas: {
    extractResponse: PassportSchemas.extractResponse,
    warnResponse: PassportSchemas.warnResponse,
    validateResponse: PassportSchemas.validateResponse,
    storeResponse: PassportSchemas.storeResponse,
  },

  // Toast messages
  toastMessages: {
    success: "Le passeport a bien été validé.",
  },

  // Form fields
  formFields: [
    {
      key: "last_name",
      label: PASSPORT_CONFIG.fieldLabels.last_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "first_name",
      label: PASSPORT_CONFIG.fieldLabels.first_name,
      type: "text",
      colSpan: 1,
    },
    {
      key: "sex",
      label: PASSPORT_CONFIG.fieldLabels.sex,
      type: "select",
      options: [
        { value: "male", label: "Masculin" },
        { value: "female", label: "Féminin" },
        { value: "neutral", label: "Neutre" },
      ],
      isSearchable: false,
      colSpan: 1,
    },
    {
      key: "document_number",
      label: PASSPORT_CONFIG.fieldLabels.document_number,
      type: "text",
      placeholder: "Ex : 12AB34567",
      colSpan: 1,
    },
    {
      key: "expiration_date",
      label: PASSPORT_CONFIG.fieldLabels.expiration_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "issue_date",
      label: PASSPORT_CONFIG.fieldLabels.issue_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "birth_date",
      label: PASSPORT_CONFIG.fieldLabels.birth_date,
      type: "date",
    },
    {
      key: "birth_place_city",
      label: PASSPORT_CONFIG.fieldLabels.birth_place_city,
      type: "text",
      placeholder: "Ex : Paris",
    },
    {
      key: "birth_place_country_code",
      label: PASSPORT_CONFIG.fieldLabels.birth_place_country_code,
      type: "select",
      options: countryOptions(),
    },
    {
      key: "nationality_country_code",
      label: PASSPORT_CONFIG.fieldLabels.nationality_country_code,
      type: "select",
      options: nationalityOptions(),
    },
  ],

  // Transform OCR extraction to form data
  transformExtraction: (extraction: PassportExtraction): Partial<PassportData> => {
    // Validation (throws on issues)
    if (!extraction.isValidDocumentType) {
      throw new Error(
        "Les fichiers ajoutés ne semblent pas correspondre à un passeport. Vérifie que ce soit bien le cas."
      );
    }

    // Note: No hasRecto/hasVerso validation for passport (single page document)

    // Transformation (only non-null values)
    const result: Partial<PassportData> = {};

    if (extraction.first_name) result.first_name = formatFirstName(extraction.first_name);
    if (extraction.last_name) result.last_name = formatLastName(extraction.last_name);
    if (extraction.birth_date) result.birth_date = extraction.birth_date;
    if (extraction.birth_place_city) result.birth_place_city = extraction.birth_place_city;
    if (extraction.birth_place_country_code) result.birth_place_country_code = extraction.birth_place_country_code;
    if (extraction.sex) result.sex = extraction.sex;
    if (extraction.nationality_country_code) result.nationality_country_code = extraction.nationality_country_code;
    if (extraction.issue_date) result.issue_date = extraction.issue_date;
    if (extraction.expiration_date) result.expiration_date = extraction.expiration_date;
    if (extraction.document_number) result.document_number = extraction.document_number;

    return result;
  },
});
